
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRSVP } from '../context/RSVPContext';
import { GuestData } from '../types';

export const useUserRSVPData = (eventId: string) => {
  const { 
    setSelectedGuest,
    setRsvpResponse,
    setSelectedDates,
    setSelectedTimes,
    setFeedback,
    setPhoneNumber
  } = useRSVP();

  const [fetchingUserRSVP, setFetchingUserRSVP] = useState(true);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  // Fetch the user's existing RSVP details
  useEffect(() => {
    if (eventId) {
      const fetchUserRSVP = async () => {
        try {
          setFetchingUserRSVP(true);
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          
          if (!user) {
            throw new Error("No authenticated user found");
          }
          
          // First try to find by user_id
          let { data: invite } = await supabase
            .from('event_invites')
            .select(`
              invite_id,
              display_name,
              phone_number,
              rsvp_status,
              selected_dates,
              selected_times,
              feedback_text,
              responded_at,
              claimed
            `)
            .eq('event_id', eventId)
            .eq('claimed_by_user_id', user.id)
            .maybeSingle();
            
          // If not found by user_id, try to find by phone number
          if (!invite) {
            // Get user's phone number
            const { data: userProfile } = await supabase
              .from('users')
              .select('phone_number')
              .eq('user_id', user.id)
              .single();
              
            if (userProfile?.phone_number) {
              // Try to find invite by phone
              ({ data: invite } = await supabase
                .from('event_invites')
                .select(`
                  invite_id,
                  display_name,
                  phone_number,
                  rsvp_status,
                  selected_dates,
                  selected_times,
                  feedback_text,
                  responded_at,
                  claimed
                `)
                .eq('event_id', eventId)
                .eq('phone_number', userProfile.phone_number)
                .maybeSingle());
            }
          }
          
          if (invite) {
            // Create guest data from invite
            const guestData: GuestData = {
              inviteId: invite.invite_id,
              displayName: invite.display_name,
              phoneNumber: invite.phone_number || '',
              hasResponded: !!invite.responded_at,
              rsvpStatus: invite.rsvp_status as 'accepted' | 'declined' | null,
              claimed: invite.claimed || false // Make sure claimed property is included
            };
            
            // Set the existing data in context
            setSelectedGuest(guestData);
            setRsvpResponse(invite.rsvp_status === 'accepted' ? 'yes' : 
                           invite.rsvp_status === 'declined' ? 'no' : null);
            
            if (invite.selected_dates && invite.selected_dates.length > 0) {
              setSelectedDates(invite.selected_dates);
            }
            
            if (invite.selected_times && invite.selected_times.length > 0) {
              setSelectedTimes(invite.selected_times);
            }
            
            if (invite.feedback_text) {
              setFeedback(invite.feedback_text);
            }
            
            if (invite.phone_number) {
              setPhoneNumber(invite.phone_number);
            }
          } else {
            throw new Error("No RSVP found for this user");
          }
        } catch (error) {
          console.error("Error fetching user RSVP:", error);
          setFetchError(error instanceof Error ? error : new Error("Failed to load RSVP data"));
        } finally {
          setFetchingUserRSVP(false);
        }
      };
      
      fetchUserRSVP();
    }
  }, [eventId, setSelectedGuest, setRsvpResponse, setSelectedDates, setSelectedTimes, setFeedback, setPhoneNumber]);

  return {
    fetchingUserRSVP,
    fetchError
  };
};
